import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import {
  ClipboardCheck,
  MessageCircle,
  MessageSquareReply,
  PhoneCall,
  CalendarCheck,
  Send,
  Sparkles,
  Loader2,
  AtSign,
  StickyNote,
  Users,
} from 'lucide-react';
import { ApiError, apiRequest } from '@/lib/api';
import { useCommentReply } from '@/hooks/useConversations';
import { isWindowOpen, windowLabel } from '@/lib/replyWindow';

type InteractionKind =
  | 'audit'
  | 'comment_in'
  | 'comment_out'
  | 'dm_in'
  | 'dm_out'
  | 'mention'
  | 'aria_call'
  | 'rdv_booked'
  | 'mars_pushed'
  | 'stage_change'
  | 'note';

interface Interaction {
  id: string;
  kind: InteractionKind;
  direction: 'inbound' | 'outbound' | 'system' | null;
  content: string | null;
  meta: Record<string, unknown>;
  /** Lot 3.1 — post + commentaire côté réseau (permet la réponse privée). */
  external_ref?: { postId?: string; commentId?: string; providerAccountId?: string } | null;
  occurred_at: string;
}

const KIND_META: Record<
  InteractionKind,
  { labelKey: string; icon: typeof MessageCircle; tint: string; bg: string }
> = {
  audit: { labelKey: 'timeline.kinds.audit', icon: ClipboardCheck, tint: '#8b5cf6', bg: 'bg-violet-50' },
  comment_in: { labelKey: 'timeline.kinds.comment_in', icon: MessageCircle, tint: '#3b82f6', bg: 'bg-blue-50' },
  comment_out: { labelKey: 'timeline.kinds.comment_out', icon: MessageSquareReply, tint: '#3b82f6', bg: 'bg-blue-50' },
  dm_in: { labelKey: 'timeline.kinds.dm_in', icon: MessageCircle, tint: '#2563eb', bg: 'bg-blue-50' },
  dm_out: { labelKey: 'timeline.kinds.dm_out', icon: Send, tint: '#2563eb', bg: 'bg-blue-50' },
  mention: { labelKey: 'timeline.kinds.mention', icon: AtSign, tint: '#a855f7', bg: 'bg-purple-50' },
  aria_call: { labelKey: 'timeline.kinds.aria_call', icon: PhoneCall, tint: '#d946ef', bg: 'bg-fuchsia-50' },
  rdv_booked: { labelKey: 'timeline.kinds.rdv_booked', icon: CalendarCheck, tint: '#06b6d4', bg: 'bg-cyan-50' },
  mars_pushed: { labelKey: 'timeline.kinds.mars_pushed', icon: Users, tint: '#f59e0b', bg: 'bg-amber-50' },
  stage_change: { labelKey: 'timeline.kinds.stage_change', icon: Sparkles, tint: '#ec4899', bg: 'bg-pink-50' },
  note: { labelKey: 'timeline.kinds.note', icon: StickyNote, tint: '#64748b', bg: 'bg-slate-50' },
};

function formatTime(iso: string, t: TFunction, locale: string): string {
  const d = new Date(iso);
  const now = new Date();
  const diffMs = now.getTime() - d.getTime();
  const mins = Math.floor(diffMs / 60_000);
  if (mins < 1) return t('timeline.now');
  if (mins < 60) return t('timeline.minsAgo', { count: mins });
  const hours = Math.floor(mins / 60);
  if (hours < 24) return t('timeline.hoursAgo', { count: hours });
  const days = Math.floor(hours / 24);
  if (days < 7) return t('timeline.daysAgo', { count: days });
  return d.toLocaleDateString(locale, { day: 'numeric', month: 'short', year: 'numeric' });
}

export function Timeline({ prospectId }: { prospectId: string }) {
  const { t, i18n } = useTranslation();
  const [items, setItems] = useState<Interaction[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    apiRequest<Interaction[]>(`/prospect/${prospectId}/timeline`)
      .then((data) => {
        if (!cancelled) setItems(data);
      })
      .catch((err) => {
        if (!cancelled) setError((err as Error).message);
      });
    return () => {
      cancelled = true;
    };
  }, [prospectId]);

  if (error) {
    return <p className="py-6 text-center text-[13px] text-ink/50">{error}</p>;
  }
  if (items === null) {
    return (
      <div className="flex justify-center py-8">
        <Loader2 className="animate-spin text-pink" size={22} />
      </div>
    );
  }
  if (items.length === 0) {
    return (
      <p className="py-6 text-center text-[13px] text-ink/50">
        {t('timeline.empty')}
      </p>
    );
  }

  return (
    <ol className="relative space-y-3 pl-6 before:absolute before:left-[10px] before:top-2 before:bottom-2 before:w-px before:bg-ink/10">
      {items.map((it) => {
        const meta = KIND_META[it.kind] ?? KIND_META.note;
        const Icon = meta.icon;
        return (
          <li key={it.id} className="relative">
            <span
              className={`absolute -left-6 top-1 flex h-[22px] w-[22px] items-center justify-center rounded-full ring-2 ring-cream ${meta.bg}`}
              style={{ color: meta.tint }}
            >
              <Icon size={12} />
            </span>
            <div className="rounded-xl border border-ink/[0.06] bg-white px-3.5 py-2.5">
              <div className="mb-1 flex items-center justify-between gap-3">
                <span className="text-[11px] font-700 uppercase tracking-wide" style={{ color: meta.tint }}>
                  {t(meta.labelKey)}
                </span>
                <time className="font-mono text-[10px] text-ink/45">{formatTime(it.occurred_at, t, i18n.language)}</time>
              </div>
              {it.content && (
                <p className="whitespace-pre-wrap text-[13px] text-ink/80 leading-snug">
                  {it.content}
                </p>
              )}
              {it.kind === 'comment_in' && it.external_ref?.commentId ? (
                <PrivateReply interaction={it} onSent={() => setItems((prev) => prev)} />
              ) : null}
              {it.kind === 'rdv_booked' && it.meta?.starts_at ? (
                <p className="mt-1 font-mono text-[11px] text-cyan-700">
                  {new Date(String(it.meta.starts_at)).toLocaleString(i18n.language, {
                    weekday: 'short',
                    day: 'numeric',
                    month: 'short',
                    hour: '2-digit',
                    minute: '2-digit',
                  })}
                </p>
              ) : null}
            </div>
          </li>
        );
      })}
    </ol>
  );
}

/**
 * Lot 3.1 — répondre EN PRIVÉ à l'auteur d'un commentaire (règle Meta : un
 * commentaire ouvre une fenêtre de 24 h, comme un message). Rien ne part sans
 * le membre ; hors fenêtre le formulaire disparaît.
 */
function PrivateReply({ interaction, onSent }: { interaction: Interaction; onSent: () => void }) {
  const { t, i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const [text, setText] = useState('');
  const [done, setDone] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const reply = useCommentReply();
  const openWindow = isWindowOpen(interaction.occurred_at);

  if (done) {
    return <p className="mt-1.5 text-[11.5px] font-700 text-emerald-700">{t('conversations.privateReplySent')}</p>;
  }
  if (!openWindow) {
    return <p className="mt-1.5 text-[11px] text-ink/45">{t('conversations.windowClosed')}</p>;
  }
  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="mt-1.5 inline-flex items-center gap-1 rounded-lg bg-blue-50 px-2 py-1 text-[11px] font-700 text-blue-700 transition hover:bg-blue-100"
      >
        <MessageSquareReply size={11} /> {t('conversations.privateReply')}
        <span className="font-400 text-blue-700/60">· {windowLabel(interaction.occurred_at, i18n.language)}</span>
      </button>
    );
  }
  return (
    <div className="mt-1.5">
      <textarea
        value={text}
        onChange={(e) => setText(e.target.value)}
        rows={2}
        autoFocus
        placeholder={t('conversations.privateReply')}
        className="w-full resize-none rounded-lg border border-ink/10 bg-white px-2.5 py-1.5 text-[12.5px] outline-none focus:border-blue-300"
      />
      <div className="mt-1 flex items-center gap-2">
        <button
          type="button"
          disabled={!text.trim() || reply.isPending}
          onClick={() => {
            setError(null);
            reply.mutate(
              { interactionId: interaction.id, text: text.trim() },
              {
                onSuccess: () => { setDone(true); onSent(); },
                onError: (e) => setError(e instanceof ApiError ? e.message : t('conversations.sendError')),
              },
            );
          }}
          className="inline-flex items-center gap-1 rounded-lg bg-blue-600 px-2.5 py-1 text-[11.5px] font-700 text-white disabled:opacity-50"
        >
          {reply.isPending ? <Loader2 size={11} className="animate-spin" /> : <Send size={11} />} {t('common.send')}
        </button>
        <button type="button" onClick={() => { setOpen(false); setText(''); }} className="text-[11.5px] font-600 text-ink/50">
          {t('common.cancel')}
        </button>
      </div>
      {error && <p className="mt-1 text-[11px] text-red-600">{error}</p>}
    </div>
  );
}

export { type Interaction };
